import { useState } from 'react';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import type { BillingPlan } from '@yawi/types';
import { Button, Card, Badge } from '@/core/ui/components';
import { useConfirmation } from '@/app/providers';
import { useBillingPlans } from '../hooks/useBilling';
import { PlanWizard } from './wizard';

function formatBytes(bytes: number | string): string {
  const num = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
  if (!num || isNaN(num)) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return parseFloat((num / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatPrice(price: number | string): string {
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
}

const featureLabels: Record<string, string> = {
  basicReports: 'Reportes básicos',
  advancedReports: 'Reportes avanzados',
  apiAccess: 'Acceso API',
  customBranding: 'Personalización de marca',
  multipleAdmins: 'Múltiples administradores',
  prioritySupport: 'Soporte prioritario',
  autoBackup: 'Backup automático',
  multiLanguage: 'Multi-idioma',
  mobileAccess: 'Acceso móvil',
  unlimitedIntegrations: 'Integraciones ilimitadas',
};

export function PlansTab() {
  const { confirm } = useConfirmation();
  const { plans, isLoading, deletePlan, refetch } = useBillingPlans();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BillingPlan | undefined>();

  const handleEdit = (plan: BillingPlan) => {
    setEditingPlan(plan);
    setShowFormModal(true);
  };

  const handleDelete = async (plan: BillingPlan) => {
    const confirmed = await confirm({
      title: 'Delete Plan',
      message: `Are you sure you want to delete "${plan.name}"? Tenants using this plan will need to be migrated first.`,
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (confirmed) {
      await deletePlan(plan.id);
    }
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setEditingPlan(undefined);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-surface rounded-xl border border-theme p-6 animate-pulse">
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-4" />
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-4 bg-slate-200 dark:bg-slate-700 rounded" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowFormModal(true)}
        >
          Add Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const features = plan.features ?? {};
          const enabledFeatures = Object.entries(features).filter(([, v]) => v);

          return (
            <Card key={plan.id} className="relative">
              {plan.sortOrder === 2 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="purple">Popular</Badge>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-theme">{plan.name}</h3>
                <p className="text-sm text-theme-secondary mt-1">{plan.description}</p>
                <Badge variant={plan.isActive ? 'success' : 'default'} className="mt-2">
                  {plan.code}
                </Badge>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-theme">
                    ${formatPrice(plan.basePrice)}
                  </span>
                  <span className="text-theme-secondary">
                    /{plan.billingCycle === 'MONTHLY' ? 'mo' : 'yr'}
                  </span>
                </div>
                {plan.trialDays > 0 && (
                  <p className="text-xs text-theme-secondary mt-1">
                    {plan.trialDays} días de prueba
                  </p>
                )}
              </div>

              {enabledFeatures.length > 0 && (
                <div className="space-y-3 mb-6">
                  {enabledFeatures.map(([key]) => (
                    <div key={key} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm text-theme-secondary">
                        {featureLabels[key] || key}
                      </span>
                    </div>
                  ))}
                  {Object.entries(features).filter(([, v]) => !v).length > 0 && (
                    <div className="pt-2">
                      {Object.entries(features).filter(([, v]) => !v).map(([key]) => (
                        <div key={key} className="flex items-start gap-2 opacity-40">
                          <X className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                          <span className="text-sm text-theme-secondary line-through">
                            {featureLabels[key] || key}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-6 pt-4 border-t border-theme">
                <h4 className="text-sm font-medium text-theme">Límites Incluidos</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-theme-secondary">Unidades:</div>
                  <div className="text-theme font-medium">
                    {plan.includedUnits === -1 ? 'Ilimitado' : plan.includedUnits}
                  </div>
                  <div className="text-theme-secondary">Usuarios:</div>
                  <div className="text-theme font-medium">
                    {plan.includedUsers === -1 ? 'Ilimitado' : plan.includedUsers}
                  </div>
                  <div className="text-theme-secondary">Almacenamiento:</div>
                  <div className="text-theme font-medium">
                    {formatBytes(plan.includedStorageBytes)}
                  </div>
                  <div className="text-theme-secondary">Notificaciones:</div>
                  <div className="text-theme font-medium">
                    {plan.includedNotifications === -1 ? 'Ilimitado' : plan.includedNotifications.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6 pt-4 border-t border-theme">
                <h4 className="text-sm font-medium text-theme">Precios Extra</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-theme-secondary">Por unidad extra:</div>
                  <div className="text-theme">${plan.extraUnitPrice}/mes</div>
                  <div className="text-theme-secondary">Por usuario extra:</div>
                  <div className="text-theme">${plan.extraUserPrice}/mes</div>
                  <div className="text-theme-secondary">Storage extra:</div>
                  <div className="text-theme">${plan.extraStoragePricePerGb}/GB</div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  leftIcon={<Edit className="w-4 h-4" />}
                  onClick={() => handleEdit(plan)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => handleDelete(plan)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          );
        })}

        {plans.length === 0 && (
          <div className="col-span-3 text-center py-12">
            <p className="text-theme-secondary">No plans configured yet</p>
            <Button
              className="mt-4"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowFormModal(true)}
            >
              Create First Plan
            </Button>
          </div>
        )}
      </div>

      {showFormModal && (
        <PlanWizard
          plan={editingPlan}
          onClose={handleCloseModal}
          onSuccess={() => {
            handleCloseModal();
            refetch();
          }}
        />
      )}
    </div>
  );
}
